import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Share2 } from 'lucide-react';
import type { ExamScaledReport } from '@examing/types';
import { Button } from 'components/ui/button';
import { useToast } from 'components/ui/use-toast';
import { drawShareCard, shareCardData } from './shareCard';

function toBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
}

/**
 * Makes a picture of the IELTS band or SAT score and opens the phone's share sheet (Instagram, Telegram),
 * or downloads it where sharing files is not supported. Renders nothing until there is a final score.
 */
export function ShareScoreButton({ report, testTitle, className }: { report: ExamScaledReport; testTitle: string; className?: string }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const data = shareCardData(report, testTitle);
  if (!data) return null;

  const share = async () => {
    setBusy(true);
    try {
      const canvas = document.createElement('canvas');
      drawShareCard(canvas, data, { score: t('share.score_label'), cta: t('share.cta') });
      const blob = await toBlob(canvas);
      if (!blob) throw new Error('No image');
      const file = new File([blob], `examing-${data.exam.toLowerCase()}-${data.headline}.png`, { type: 'image/png' });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], text: t('share.text', { exam: data.exam, score: data.headline }) });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.name;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      // Closing the share sheet is not an error.
      if ((error as { name?: string })?.name !== 'AbortError') toast({ title: t('share.failed'), variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button type="button" variant="outline" size="sm" className={className} onClick={share} disabled={busy}>
      {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden /> : <Share2 className="mr-1.5 h-4 w-4" aria-hidden />}
      {t('share.button')}
    </Button>
  );
}

export default ShareScoreButton;
